import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./styles.scss";
import { Badge, Button } from "@fluentui/react-components";
import { ArrowClockwiseRegular, ShieldPersonRegular } from "@fluentui/react-icons";
import Paginator from "#root/components/Paginator";
import { useIncidentById } from "#root/queries/incidents";
import { usePersons } from "#root/queries/persons";
import LoadingWrapper from "#root/components/LoadingWrapper";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { PERSON_ENUMS } from "#root/utils/persons";
import { getEnumLabelByValue } from "#root/utils/enums";
import LazyImage from "#root/components/LazyImage";

const ITEMS_PER_PAGE = 20;

const StatusReview: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();

  const [currentPage, setCurrentPage] = useState(0);

  const {
    data: incidentResponse,
  } = useIncidentById({
    id: incidentId || "",
    includeVictimsData: true
  });

  const {
    data: personsResponse,
    isLoading: personsLoading,
    refetch: refetchPersons
  } = usePersons({
    incidentId: incidentId || "",
    type: 'victim',
    skip: currentPage * ITEMS_PER_PAGE,
    take: ITEMS_PER_PAGE
  });

  const totalCount = personsResponse?.totalCount ?? 0;
  const victims = personsResponse?.items ?? [];
  const incident = incidentResponse?.item ?? {};
  const stats = {
    matched: incident.matchedAmount ?? 0,
    notified: incident.notifiedAmount ?? 0
  }

  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / ITEMS_PER_PAGE);
  }, [totalCount]);

  const handleRefresh = () => {
    refetchPersons();
  };

  if (!incidentId) {
    return (
      <div className="status-review">
        <div className="status-review__empty">Please select an incident</div>
      </div>
    );
  }

  return (
    <div className="status-review">
      <div className="emc-page-header">
        <div className="emc-page-header__top-row">
          <div className="emc-page-header__breadcrumbs">
            <Breadcrumbs currentPageName={incident?.name && incident?.address ? `${incident.name}: ${incident.address}` : "Status Review"} />
          </div>
          <div className="emc-page-header__props">
            <Badge size="extra-large" color="success">
              Total Victim: {totalCount}
            </Badge>
            <Badge size="extra-large" color="success">
              Matched: {stats.matched}
            </Badge>
            <Badge size="extra-large" color="success" appearance="tint">
              Reunified: {stats.notified}
            </Badge>
            <Button
              appearance="primary"
              shape="circular"
              icon={<ArrowClockwiseRegular />}
              onClick={handleRefresh}
            />
          </div>
        </div>
        <div className="emc-page-header__title">
          <h2>Status Review <div className="divider" /></h2>
        </div>
      </div>

      <LoadingWrapper
        isLoading={personsLoading}
        isEmpty={victims.length === 0}
        emptyMessage={`No victims found for this incident.`}
        emptyIcon={<ShieldPersonRegular />}
      >
        <div>
          <table className="status-review__table">
            <thead>
              <tr>
                <th>Picture</th>
                <th>Name</th>
                <th>Gender</th>
                <th>Status</th>
                <th>Matched</th>
                <th>Reunification Complete</th>
              </tr>
            </thead>
            <tbody>
              {victims.map((person) => (
                <tr key={person.id}>
                  <td>
                    <div className="person-image">
                      <LazyImage
                        src={person.pictureUrl}
                        alt={person.fullName || 'Victim'}
                        fallback={<div className="person-image--no-image" />}
                      />
                    </div>
                  </td>
                  <td>{person.fullName}</td>
                  <td>{getEnumLabelByValue(PERSON_ENUMS.GENDER, person.gender)}</td>
                  <td>{person.status}</td>
                  <td>
                    <Badge className="status-badge" shape="circular" size="large" color={person.isMatched ? "success" : "danger"}>
                      {person.isMatched ? "✓" : "✕"}
                    </Badge>
                  </td>
                  <td>
                    <Badge className="status-badge" shape="circular" size="large" color={person.isConnected ? "success" : "danger"}>
                      {person.isConnected ? "✓" : "✕"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paginator
            currentPage={currentPage + 1}
            totalPages={totalPages}
            onPageChange={(newPage) => setCurrentPage(newPage - 1)}
            className="all-victims__pagination"
          />
        </div>
      </LoadingWrapper>
    </div>
  );
};

export default StatusReview;